/* ═══════════════════════════════════════════════════════════════
   Phase widget (Project Canvas R1, T2) — the phase Stepper, Set phase
   SearchableSelect, Mark/Reopen complete, and the drill into the
   current phase, moved verbatim out of the old ProjectPanel.tsx
   chrome. Manage opens the same PhasePanel Sheet (Start/End dates
   incl.). Same permissions (`ctx.can.edit`, `ctx.can.manageBilling`).

   Per-section collapse/reorder retired (Decision 12). Manage is now
   the kit's icon-only WidgetActionButton, same "Manage phases" label
   carried as its aria-label/tooltip.
   ═══════════════════════════════════════════════════════════════ */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, Lock, Settings2 } from 'lucide-react';
import type { ProjectPhase } from '../../../../shared/entities';
import { Stepper, WidgetFrame, WidgetActionButton } from '../../../components/widgets';
import type { StepperPhase } from '../../../components/widgets';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { Button } from '../../../components/ui/button';
import { Icon } from '../../../components/ui/Icon';
import EmptyState from '../../../components/EmptyState';
import { rem } from '../../../lib/rem';
import { PhasePanel } from '../panels/PhasePanel';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

function SectionLoading() {
  return (
    <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }} role="status">
      Loading…
    </div>
  );
}

function PhaseBody({ ctx, onManage }: { ctx: ProjectWidgetCtx; onManage: () => void }) {
  const [busy, setBusy] = useState(false);
  const phases = useMemo(
    () => [...ctx.phases.data].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [ctx.phases.data]
  );
  const currentIdx = phases.findIndex((p) => p.id === ctx.project.currentPhaseId);
  const current: ProjectPhase | undefined = currentIdx >= 0 ? phases[currentIdx] : undefined;
  const canSet = ctx.can.edit;
  const canManageBilling = ctx.can.manageBilling;

  const handleSet = async (phaseId: string) => {
    if (!canSet || busy) return;
    setBusy(true);
    try {
      await ctx.setCurrentPhase(phaseId);
    } finally {
      setBusy(false);
    }
  };

  const handleToggleComplete = async () => {
    if (!current || !canManageBilling || busy) return;
    setBusy(true);
    try {
      await ctx.savePhase({
        ...current,
        status: current.status === 'complete' ? 'pending' : 'complete',
      });
    } finally {
      setBusy(false);
    }
  };

  if (ctx.phases.status === 'loading') return <SectionLoading />;
  if (ctx.phases.status === 'error') {
    return (
      <EmptyState
        compact
        icon={<Icon icon={AlertTriangle} em={1.75} className="text-danger" />}
        title={ctx.phases.error || 'Could not load phases.'}
        action={{ label: 'Try again', onClick: () => void ctx.phases.reload() }}
      />
    );
  }
  if (phases.length === 0) {
    return (
      <EmptyState
        compact
        title="No phases yet"
        subtitle={canSet ? undefined : 'Ask a project editor to set up phases.'}
        action={canSet ? { label: 'Set up phases', onClick: onManage } : undefined}
      />
    );
  }

  const stepperPhases: StepperPhase[] = phases.map((p, i) => ({
    id: p.id,
    label: p.name,
    state:
      currentIdx === -1
        ? 'upcoming'
        : i < currentIdx
          ? 'done'
          : i === currentIdx
            ? 'current'
            : 'upcoming',
    meta:
      p.status === 'complete' ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: rem(4) }}>
          <Icon icon={Lock} size="xs" aria-label="Completed — time locked" /> Complete
        </span>
      ) : undefined,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/* Click/Enter the stepper to drill into the current phase — the kit
          Stepper has no per-node click, so this is the one whole-control
          substitute for it (superset merge from the pre-T3b compact view). */}
      <div
        onClick={current ? () => ctx.onDrill({ kind: 'phase', phaseId: current.id }) : undefined}
        role={current ? 'button' : undefined}
        tabIndex={current ? 0 : undefined}
        aria-label={
          current
            ? `Current phase ${current.name}, step ${currentIdx + 1} of ${phases.length}`
            : 'No phase set'
        }
        onKeyDown={(e) => {
          if (current && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            ctx.onDrill({ kind: 'phase', phaseId: current.id });
          }
        }}
        style={{ cursor: current ? 'pointer' : 'default', borderRadius: 'var(--radius-sm)' }}
      >
        <Stepper phases={stepperPhases} compact={false} />
      </div>
      {(canSet || (canManageBilling && current)) && (
        <div
          style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          {canSet && (
            <div style={{ minWidth: '12rem' }}>
              <SearchableSelect
                value={current?.id || ''}
                options={phases.map((p) => ({ value: p.id, label: p.name }))}
                onChange={(v) => void handleSet(v)}
                placeholder="Set phase…"
                clearable={false}
                disabled={busy}
              />
            </div>
          )}
          {canManageBilling && current && (
            <Button size="sm" variant="outline" onClick={handleToggleComplete} disabled={busy}>
              {current.status === 'complete' ? 'Reopen phase' : `Mark "${current.name}" complete`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function PhaseCard({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [managePhasesOpen, setManagePhasesOpen] = useState(false);
  return (
    <>
      <WidgetFrame
        title="Phase"
        status="ready"
        form="stepper"
        actions={
          ctx.can.edit ? (
            <WidgetActionButton
              icon={Settings2}
              label="Manage phases"
              onClick={() => setManagePhasesOpen(true)}
            />
          ) : undefined
        }
      >
        <PhaseBody ctx={ctx} onManage={() => setManagePhasesOpen(true)} />
      </WidgetFrame>

      <PhasePanel ctx={ctx} open={managePhasesOpen} onClose={() => setManagePhasesOpen(false)} />
    </>
  );
}

export const phaseWidget: ProjectWidgetDef = {
  id: 'project_phase',
  title: 'Phase',
  description: 'Current phase stepper, Set phase, Mark complete / Reopen phase.',
  category: 'schedule',
  cols: { min: 6, max: 12 },
  defaultSize: 'standard',
  inputSurface: true,
  render: (ctx) => <PhaseCard ctx={ctx} />,
};
